/**
 * Demo-only "Configure brand" card.
 *
 * Lets the demoer rebrand the active demo tenant's display name on the
 * fly (e.g. flip "Vitalora" to "Nintendo" before a customer meeting,
 * back to default after). Hits `PUT /catalyst/demo/brand`, which only
 * mutates `Enterprise.name` and refuses unless the target enterprise
 * is flagged `isDemo === true`.
 *
 * Resets to the seeded canonical name when the input is left empty,
 * which surfaces as the visible "Reset to default" affordance — the
 * backend's empty-string normalization handles the round trip.
 *
 * Gated behind `isDemoEnterprise()` at the call site so the card never
 * renders on a real tenant.
 */
import {
  Button,
  Card,
  Code,
  Flex,
  Heading,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { updateDemoBrandName } from "../../../services/demo";
import {
  DEFAULT_DEMO_BRAND_NAME,
  useDemoBrandName,
} from "../../../utils/demoBrand";
import { errorMessage } from "../../../utils/http";
import { useEnterprises } from "../../../utils/queries/enterprises";

type State =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "done"; appliedName: string }
  | { kind: "error"; message: string };

export function DemoBrandConfig() {
  const { t } = useTranslation("ui");
  const liveBrand = useDemoBrandName();
  const enterprisesQuery = useEnterprises();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<string>(liveBrand);
  const [state, setState] = useState<State>({ kind: "idle" });

  // Keep the input synced with whatever the live brand is when the
  // component first mounts (or when it changes from another tab via the
  // enterprise list cache). We only sync on transitions, not while the
  // operator is mid-edit; the `state.kind === "idle"` guard prevents
  // overwriting in-progress typing.
  useEffect(() => {
    if (state.kind === "idle") {
      setDraft(liveBrand);
    }
  }, [liveBrand, state.kind]);

  async function apply(nameToApply: string) {
    setState({ kind: "running" });
    try {
      const res = await updateDemoBrandName(nameToApply);
      await queryClient.invalidateQueries({ queryKey: ["enterprises"] });
      setState({ kind: "done", appliedName: res.name });
      setDraft(res.name);
    } catch (e) {
      setState({
        kind: "error",
        message: await errorMessage(e),
      });
    }
  }

  const trimmedDraft = draft.trim();
  const dirty = trimmedDraft !== liveBrand;
  const canSubmit =
    state.kind !== "running" && dirty && trimmedDraft.length > 0;
  const canReset =
    state.kind !== "running" && liveBrand !== DEFAULT_DEMO_BRAND_NAME;

  return (
    <Card data-testid="settings-card-demo-brand">
      <Flex direction="column" gap="3">
        <Flex direction="column" gap="1">
          <Heading as="h2" size="3" weight="bold">
            {t("settings.demoBrandConfig.renameDemoCompany")}
          </Heading>
          <Text size="2" color="gray">
            Change the company name shown in the demo (for example, swap
            &quot;Vitalora&quot; for the buyer&apos;s real company name before a
            meeting, then change it back afterwards). Only the visible name
            changes. Everything else (logins, files, content) stays the same.
            Click Apply to push the new name to the live session.
          </Text>
        </Flex>
        <Flex align="center" gap="3" wrap="wrap">
          <TextField.Root
            type="text"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              if (state.kind !== "idle") setState({ kind: "idle" });
            }}
            placeholder={DEFAULT_DEMO_BRAND_NAME}
            maxLength={80}
            data-testid="settings-demo-brand-input"
            className="min-w-[260px] flex-1"
          />
          <Button
            data-testid="settings-demo-brand-apply"
            onClick={() => apply(trimmedDraft)}
            disabled={!canSubmit}
          >
            {state.kind === "running"
              ? t("settings.demoBrandConfig.applying")
              : t("common:actions.apply")}
          </Button>
          <Button
            variant="soft"
            color="gray"
            data-testid="settings-demo-brand-reset"
            onClick={() => apply("")}
            disabled={!canReset}
          >
            {t("settings.demoBrandConfig.resetDefault")}
          </Button>
        </Flex>
        <Flex direction="column" gap="1">
          <Text size="2" color="gray">
            Currently showing as <Code>{liveBrand}</Code>
            {enterprisesQuery.isFetching ? " (refreshing…)" : ""}
          </Text>
          {state.kind === "done" ? (
            <Text size="2" color="green">
              Renamed to {state.appliedName}.
            </Text>
          ) : null}
          {state.kind === "error" ? (
            <Text size="2" color="red">
              {state.message}
            </Text>
          ) : null}
        </Flex>
      </Flex>
    </Card>
  );
}
